
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/context/LanguageContext';
import { Language } from '@/types/language';
import { getLocalizedPath } from '@/utils/languageUtils';
import '@/styles/emoji-flags.css';

interface LanguageSwitcherBackofficeProps {
  buttonClassName?: string;
}

const LanguageSwitcherBackoffice = ({ buttonClassName = "h-8 w-8 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200" }: LanguageSwitcherBackofficeProps) => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    
  ];

  const handleLanguageChange = (langCode: string) => {
    const lang = langCode as Language;
    
    if (lang !== language) {
      // Update context language and save to localStorage
      setLanguage(lang);
      
      // Update URL to reflect language change and preserve search parameters
      const newPath = getLocalizedPath(location.pathname, lang, location.search);
      navigate(newPath, { replace: true });
    }
    
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={buttonClassName}
          aria-label="Select language"
        >
          <Globe className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-48 bg-gray-800 border border-gray-700 text-white p-2"
      >
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            className="flex items-center justify-between cursor-pointer py-2 text-white/80 hover:text-white hover:bg-gray-700 focus:bg-grey-700 rounded-md"
            onClick={() => handleLanguageChange(lang.code)}
          >
            <div className="flex items-center gap-2">
              <span className="emoji-flag text-lg">{lang.flag}</span>
              <span className="text-white/80 hover:text-white">{lang.name}</span>
            </div>
            {language === lang.code && <Check className="h-4 w-4 text-purple-400" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcherBackoffice;
